import { useEffect, useState, useCallback } from 'react';
import {
  getEnrichmentSettings,
  patchEnrichmentSettings,
  getEnrichmentQuota,
  getLeadEnrichmentStatus,
  triggerEnrichment,
  type EnrichmentSettings,
  type QuotaResponse,
  type EnrichmentRunRow,
  type FieldProvenanceRow,
} from './api';
import { supabase } from '../supabase';

/**
 * Gli hook NON propagano mai gli errori dal fetch: catch + setError.
 * Cosi se il backend e` down / non ancora deployato, la UI degrada graceful
 * invece di buttare uncaught promise rejection.
 */

export function useEnrichmentSettings(): {
  settings: EnrichmentSettings | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  patch: (updates: Partial<EnrichmentSettings>) => Promise<EnrichmentSettings | null>;
} {
  const [settings, setSettings] = useState<EnrichmentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getEnrichmentSettings();
      setSettings(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const patch = useCallback(async (updates: Partial<EnrichmentSettings>) => {
    setError(null);
    try {
      const s = await patchEnrichmentSettings(updates);
      setSettings(s);
      return s;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return null;
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!settings?.organization_id) return undefined;
    const channel = supabase
      .channel(`enrichment-settings-${settings.organization_id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'lead_enrichment_settings',
          filter: `organization_id=eq.${settings.organization_id}`,
        },
        (payload) => {
          setSettings(payload.new as EnrichmentSettings);
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [settings?.organization_id]);

  return { settings, loading, error, reload, patch };
}

export function useEnrichmentQuota(): {
  quota: QuotaResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [quota, setQuota] = useState<QuotaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = await getEnrichmentQuota();
      setQuota(q);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setQuota(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { quota, loading, error, reload };
}

export function useLeadEnrichmentStatus(leadId: string | null): {
  runs: EnrichmentRunRow[];
  provenance: FieldProvenanceRow[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  trigger: () => Promise<{ enqueued: boolean; reason?: string }>;
} {
  const [runs, setRuns] = useState<EnrichmentRunRow[]>([]);
  const [provenance, setProvenance] = useState<FieldProvenanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getLeadEnrichmentStatus(leadId);
      setRuns(data.runs);
      setProvenance(data.field_provenance);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Manteniamo stato vuoto per render graceful
      setRuns([]);
      setProvenance([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  const trigger = useCallback(async () => {
    if (!leadId) return { enqueued: false, reason: 'no_lead' };
    try {
      const r = await triggerEnrichment(leadId);
      await reload();
      return r;
    } catch (err) {
      throw err instanceof Error ? err : new Error(String(err));
    }
  }, [leadId, reload]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime: nuovo run completato → ricarica (solo se backend raggiungibile)
  useEffect(() => {
    if (!leadId || error) return undefined;
    const channel = supabase
      .channel(`enrichment-runs-${leadId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'lead_enrichment_runs',
          filter: `lead_id=eq.${leadId}`,
        },
        () => {
          void reload();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [leadId, error, reload]);

  return { runs, provenance, loading, error, reload, trigger };
}
